import React from 'react'

import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'

import { InstitutionLogo, P, Text } from '@mxenabled/mxui'

import { ChevronRight } from '@mxenabled/mx-icons'

import { useTheme } from '@mui/material/styles'

import {
  ConnectDrawer,
  Drawer,
  useAppConfig,
  useGlobalCopyStore,
  useGlobalUiStore,
} from '../../../common'
import { type Account, type AccountType } from '../../../core'

import { getAccountDisplayName } from '../../../common/utils/AccountDisplayName'
import { getAccountBalanceDetails } from '../../../accounts'
import AccountsListCompact from '../../../accounts/components/AccountsListCompact'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

interface AccountsProps {
  loadAccounts: AccountType[]
  loadMoreLabel: string
}

const Accounts: React.FC<AccountsProps> = ({ loadAccounts, loadMoreLabel }) => {
  const theme = useTheme()
  const { config } = useAppConfig()
  const { connect: connectCopy, finstrong: copy } = useGlobalCopyStore()
  const { accounts: accountsData } = useGlobalCopyStore()
  const { selectedAccounts } = useGlobalUiStore()
  const [isOpen, setIsOpen] = React.useState(false)
  const [isConnectAccountsOpen, setIsConnectAccountsOpen] = React.useState(false)

  const accounts = React.useMemo(
    () =>
      selectedAccounts.filter((account) =>
        loadAccounts ? loadAccounts.includes(account.account_type) : true,
      ),
    [selectedAccounts, loadAccounts],
  )

  const displayedAccounts = React.useMemo(() => accounts.slice(0, 3), [accounts])

  const getAccountBalance = (account: Account) => {
    const { firstValue: accountBalance } = getAccountBalanceDetails(
      account,
      accountsData,
      config.display_available_balance_in_accounts,
    )
    return accountBalance
  }

  return accounts.length >= 1 ? (
    <>
      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        {displayedAccounts.map((account, index) => (
          <React.Fragment key={account.guid}>
            <ListItem sx={{ px: 24 }}>
              <ListItemAvatar>
                <InstitutionLogo alt="MX" institutionGuid={account.institution_guid ?? ''} />
              </ListItemAvatar>
              <ListItemText
                primary={`${getAccountDisplayName(account, {
                  allowNickname: config?.show_account_nicknames_in_master,
                })}
              ${account.account_number ? ` *${account.account_number}` : ''}`}
                slotProps={{ primary: { noWrap: true, pr: 4, variant: 'body1' } }}
              />
              <P bold={true} variant="body2">
                {formatCurrency(getAccountBalance(account), '0,0.00')}
              </P>
            </ListItem>
            {index < displayedAccounts.length - 1 && <Divider variant="inset" />}
          </React.Fragment>
        ))}
      </Box>
      {accounts.length > 3 && (
        <>
          <Box>
            <Button
              endIcon={<ChevronRight />}
              onClick={() => setIsOpen(true)}
              size="small"
              sx={{ my: 0, px: 16, py: 12, width: '100%' }}
            >
              <Text bold={true} variant="body1">
                {accounts.length - 3} {loadMoreLabel}
              </Text>
            </Button>
          </Box>
          <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} title={copy.accounts}>
            <AccountsListCompact loadAccounts={loadAccounts} />
          </Drawer>
        </>
      )}
    </>
  ) : (
    <>
      <Box>
        <Button
          onClick={() => setIsConnectAccountsOpen(true)}
          size="small"
          sx={{ my: 0, px: 16, py: 12, width: '100%' }}
          variant="contained"
        >
          <Text bold={true} variant="body1">
            {copy.connect_more_accounts_button}
          </Text>
        </Button>
      </Box>
      <ConnectDrawer
        onClose={() => setIsConnectAccountsOpen(false)}
        showConnectWidget={isConnectAccountsOpen}
        title={connectCopy.mini_title}
      />
    </>
  )
}

export default observer(Accounts)
